import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Mail, ShieldCheck, Smartphone } from "lucide-react";
import { sendVerificationOtpService, verifyVerificationOtpService } from "../../api/auth";
import { useDocumentTitle } from "../../utils/Common";
import { useTheme } from "../../utils/theme";
import { reduxFetchUserLoginDetail } from "../../redux/auth/auth";

const maskEmail = (email = "") => {
  const value = String(email || "");
  const [name, domain] = value.split("@");
  if (!name || !domain) return value;
  return `${name.slice(0, 2)}${"*".repeat(Math.max(name.length - 2, 2))}@${domain}`;
};

const maskMobile = (mobile = "") => {
  const value = String(mobile);
  if (value.length < 4) return value;
  return `${"*".repeat(value.length - 4)}${value.slice(-4)}`;
};

export default function Verify() {
  const { type } = useParams();
  const resendTimerKey = `verification-otp-resend-until:${type}`;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetail = useSelector((state) => state.auth.userDetail);
  const { isDark } = useTheme();
  const [otp, setOtp] = useState("");
  const [resendUntil, setResendUntil] = useState(() => {
    const savedDeadline = Number(sessionStorage.getItem(resendTimerKey));
    const deadline =
      savedDeadline > Date.now() ? savedDeadline : Date.now() + 120000;
    sessionStorage.setItem(resendTimerKey, String(deadline));
    return deadline;
  });
  const [resendSeconds, setResendSeconds] = useState(() =>
    Math.max(Math.ceil((resendUntil - Date.now()) / 1000), 0),
  );
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState("");
  const [resendSuccess, setResendSuccess] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const isEmail = type === "email";
  const isMobile = type === "mobile";
  const verificationEmail = isEmail
    ? sessionStorage.getItem("pending-verification-email") || userDetail.email
    : "";

  useDocumentTitle(`${isEmail ? "Email" : "Mobile"} Verification`);

  useEffect(() => {
    const updateCountdown = () => {
      setResendSeconds(
        Math.max(Math.ceil((resendUntil - Date.now()) / 1000), 0),
      );
    };

    updateCountdown();
    const timer = window.setInterval(() => {
      updateCountdown();
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendUntil]);

  useEffect(() => {
    if (!verificationError) return undefined;

    const timer = window.setTimeout(() => {
      setVerificationError("");
      setOtp("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [verificationError]);

  useEffect(() => {
    if (!resendSuccess) return undefined;

    const timer = window.setTimeout(() => {
      setResendSuccess("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [resendSuccess]);

  useEffect(() => {
    if (!resendError) return undefined;

    const timer = window.setTimeout(() => {
      setResendError("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [resendError]);

  if (!isEmail && !isMobile) {
    return <Navigate to="/profile" replace />;
  }

  const destination = isEmail
    ? maskEmail(verificationEmail)
    : maskMobile(userDetail.mobile);
  const VerificationIcon = isEmail ? Mail : Smartphone;
  const resendTime = `${String(Math.floor(resendSeconds / 60)).padStart(2, "0")}:${String(
    resendSeconds % 60,
  ).padStart(2, "0")}`;

  const handleResendCode = async () => {
    const payload = isEmail
      ? {
          type: "email",
          email: verificationEmail,
        }
      : {
          type: "mobile",
          countryCode: userDetail.countryCode || "+91",
          mobile: userDetail.mobile,
          otpVia: "text",
        };

    try {
      setIsResending(true);
      setResendError("");
      setResendSuccess("");
      await sendVerificationOtpService(payload);
      const nextDeadline = Date.now() + 120000;
      sessionStorage.setItem(resendTimerKey, String(nextDeadline));
      setResendUntil(nextDeadline);
      setOtp("");
      setResendSuccess("A new verification code has been sent.");
    } catch (error) {
      setResendError(
        error.response?.data?.message ||
          error.message ||
          "Unable to resend the verification code. Please try again.",
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    const payload = isEmail
      ? {
          type: "email",
          email: verificationEmail,
          otp,
        }
      : {
          type: "mobile",
          countryCode: userDetail.countryCode || "+91",
          mobile: userDetail.mobile,
          otp,
        };

    try {
      setIsVerifying(true);
      setVerificationError("");
      await verifyVerificationOtpService(payload);
      sessionStorage.removeItem(resendTimerKey);
      if (isEmail) sessionStorage.removeItem("pending-verification-email");
      await dispatch(reduxFetchUserLoginDetail()).unwrap();
      navigate("/profile", { replace: true });
    } catch (error) {
      setVerificationError(
        error.response?.data?.message ||
          error.message ||
          "Unable to verify OTP. Please check the code and try again.",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    // <Layout>
      <section
        className={
          isDark
            ? "welcome-hero-dark-section"
            : "welcome-hero-light"
        }
      >
        <div
          className="container d-flex align-items-center justify-content-center py-5"
          style={{ minHeight: "70vh" }}
        >
          <div className="w-100" style={{ maxWidth: "460px" }}>
            <div
              className={`rounded-4 border shadow-sm p-4 p-md-5 text-center ${
                isDark ? "welcome-hero-dark" : "bg-body"
              }`}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: "58px",
                  height: "58px",
                  color: "#16864b",
                  backgroundColor: "rgba(22, 134, 75, 0.1)",
                }}
              >
                <VerificationIcon size={28} />
              </div>

              <h2 className="h4 fw-bold text-body-emphasis">
                Verify your {isEmail ? "email address" : "mobile number"}
              </h2>
              <p className="text-body-secondary mb-1">
                We sent a one-time verification code to
              </p>
              <p className="fw-semibold text-body-emphasis mb-4">
                {destination}
              </p>

              <label htmlFor="verification-otp" className="form-label fw-semibold">
                Verification code
              </label>
              <input
                id="verification-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="form-control form-control-lg text-center fw-bold"
                placeholder="Enter OTP"
                value={otp}
                maxLength={4}
                onChange={(event) =>
                  setOtp(event.target.value.replace(/\D/g, ""))
                }
                style={{ letterSpacing: "0.35rem" }}
              />

              <button
                type="button"
                className="btn btn-success w-100 fw-semibold mt-3 py-2"
                onClick={handleVerifyOtp}
                disabled={otp.length < 4 || isVerifying}
              >
                <ShieldCheck size={18} className="me-2" />
                {isVerifying ? "Verifying..." : "Verify OTP"}
              </button>

              {verificationError && (
                <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                  {verificationError}
                </div>
              )}

              <p className="small text-body-secondary mt-4 mb-2">
                Didn&apos;t receive a code? Check your spam folder or request a
                new code.
              </p>
              <button
                type="button"
                className="btn btn-link btn-sm text-success"
                onClick={handleResendCode}
                disabled={resendSeconds > 0 || isResending}
              >
                {isResending
                  ? "Sending..."
                  : resendSeconds > 0
                    ? `Resend code in ${resendTime}`
                    : "Resend code"}
              </button>

              {resendSuccess && (
                <div className="alert alert-success py-2 mt-3 mb-0" role="status">
                  {resendSuccess}
                </div>
              )}
              {resendError && (
                <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                  {resendError}
                </div>
              )}

              <div className="border-top mt-3 pt-3">
                <Link to="/profile" className="small text-decoration-none">
                  Back to profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    // </Layout>
  );
}
